import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../api/client'
import { asignacionApi } from '../api/zonas'
import { salidasApi } from '../api/salidas'
import { remitosApi } from '../api/remitos'
import Boton from './Boton'
import FrenoEntregaAnticipada from './FrenoEntregaAnticipada'
import { useAuth } from '../context/AuthContext'
import { claveBorrador, leerBorrador, guardarBorrador, borrarBorrador } from '../utils/borradorLocal'
import { fmtHoraDe } from '../utils/fecha'
import { colors } from '../design/tokens'

/**
 * Modal de "asignación rápida": el operario escanea códigos de barras
 * (numero_presea de T90 / numero_ne de T80), elige un fletero, fecha,
 * y al confirmar se crea la salida + se agregan los docs + se despacha,
 * abre la hoja de ruta en otra pestaña y redirige a /logistica/distribucion.
 *
 * Hecho 24/06/26 a pedido del usuario.
 *
 * Borrador (sugerencia #102, 20 oct 2026): lo escaneado + destino + fecha +
 * notas se guardan en este navegador mientras se arma. Si la pestaña se
 * recarga (versión nueva, F5), al reabrir el modal se recupera y cada
 * documento se vuelve a consultar al backend (mismo buscar-codigo del escaneo):
 * el que ya no está disponible se saca y se avisa cuál. Se borra al despachar,
 * al pasar a retiro, al ir a cotizar Andreani o al cerrar a propósito.
 * `onEnCurso(bool)` le avisa a la página si hay trabajo sin despachar.
 */
export default function ModalAsignacionRapida({ open, onClose, onEnCurso }) {
  const navigate = useNavigate()
  const { realUser } = useAuth()
  const clave = claveBorrador('asignacion_rapida', realUser?.id)
  const [recuperado, setRecuperado] = useState(null) // {guardado_at, quitados:[{numero, motivo}]}
  const hidratadoRef = useRef(false)
  const [codigo, setCodigo] = useState('')
  const [items, setItems] = useState([])           // {tipo, id, numero, cliente|destinatario, direccion, zona}
  const [fleteros, setFleteros] = useState([])
  const [fleteroId, setFleteroId] = useState('')
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10))
  const [notas, setNotas] = useState('')
  const [mensaje, setMensaje] = useState(null)     // {tipo: 'ok'|'error', texto}
  const [buscando, setBuscando] = useState(false)
  const [sucursales, setSucursales] = useState([])
  const [despachando, setDespachando] = useState(false)
  const [frenos, setFrenos] = useState([])          // envíos con fecha pactada posterior (entrega anticipada)
  const inputRef = useRef(null)

  // Foco automático al input cada vez que se abre el modal
  useEffect(() => {
    if (open) {
      setCodigo(''); setItems([]); setFleteroId(''); setNotas(''); setMensaje(null); setRecuperado(null)
      setFecha(new Date().toISOString().slice(0, 10))
      hidratadoRef.current = false
      // Borrador de hoy: se hidrata y se re-valida cada documento contra el
      // backend antes de habilitar el guardado (si no, el efecto de guardar
      // pisaría el borrador con la lista vacía).
      const b = leerBorrador(clave)
      if (b && Array.isArray(b.items) && b.items.length > 0) {
        if (b.fleteroId) setFleteroId(b.fleteroId)
        if (b.fecha) setFecha(b.fecha)
        if (b.notas) setNotas(b.notas)
        setItems(b.items)
        revalidar(b.items).then(({ vigentes, quitados }) => {
          hidratadoRef.current = true   // antes del setItems: así el guardado ya refleja lo validado
          setItems(vigentes)
          setRecuperado({ guardado_at: b.guardado_at, quitados })
        })
      } else {
        hidratadoRef.current = true
      }
      setTimeout(() => inputRef.current?.focus(), 100)
      // Cargar fleteros una vez
      // Sin canales internos: un "envío" interno es un RETIRO en sucursal, no
      // una salida (Martin, 25 ago 2026). Las sucursales van como destino.
      api.get('/fleteros').then(({ data }) => {
        const activos = (Array.isArray(data) ? data : data.data || []).filter(f => f.activo !== false && !f.interno)
        setFleteros(activos)
      }).catch(() => setFleteros([]))
      api.get('/sucursales-retiro').then(({ data }) => setSucursales(data || [])).catch(() => setSucursales([]))
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // Guardar el borrador a medida que se arma (solo después de hidratar).
  useEffect(() => {
    if (!open || !hidratadoRef.current) return
    if (items.length === 0) borrarBorrador(clave)
    else guardarBorrador(clave, realUser, { items, fleteroId, fecha, notas })
  }, [open, items, fleteroId, fecha, notas, clave, realUser])

  // Trabajo en curso = documentos escaneados sin despachar.
  useEffect(() => {
    onEnCurso?.(open && items.length > 0)
    return () => onEnCurso?.(false)   // al desmontar (la página pasa a "Cargando…") no queda colgado
  }, [open, items.length, onEnCurso])

  // Entrega antes de la fecha pactada (30 oct 2026): se avisa APENAS se
  // escanea el envío, no al apretar Despachar. Retiro no cuenta; sin fletero
  // todavía, cuenta como reparto propio.
  const firmaVerificacion = JSON.stringify([open, fecha, fleteroId, items.map(it => `${it.tipo}:${it.id}`)])
  useEffect(() => {
    let vigente = true
    const t = setTimeout(async () => {
      if (!open || !items.length || String(fleteroId).startsWith('retiro:')) { setFrenos([]); return }
      try {
        const { data } = await salidasApi.verificarAnticipadas({
          fletero_id: fleteroId ? parseInt(fleteroId, 10) : null,
          fecha,
          documentos: items.map(it => ({ entidad_tipo: it.tipo, entidad_id: it.id })),
        })
        if (vigente) setFrenos(data.entrega_anticipada || [])
      } catch { /* si falla, el freno del backend al despachar sigue en pie */ }
    }, 300)
    return () => { vigente = false; clearTimeout(t) }
  }, [firmaVerificacion]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null

  // Cierre a propósito (✕ / Cancelar): se descarta el borrador.
  const cerrar = () => {
    if (items.length > 0 && !window.confirm(`¿Descartar los ${items.length} documento(s) escaneado(s)? La asignación rápida se pierde.`)) return
    borrarBorrador(clave)
    onClose()
  }

  const agregar = async () => {
    const c = codigo.trim()
    if (!c) return
    setBuscando(true); setMensaje(null)
    try {
      // Dedupe local: si ya está en la lista, no lo agrego
      const ya = items.find(it => it.tipo + ':' + it.id === c || it.numero === c)
      if (ya) {
        setMensaje({ tipo: 'error', texto: `Ya escaneaste ${c} en esta sesión.` })
        setCodigo(''); return
      }
      const { data } = await asignacionApi.buscarCodigo(c)
      setItems(prev => [data, ...prev])
      setMensaje({ tipo: 'ok', texto: `✓ ${data.numero} agregado` })
      setCodigo('')
    } catch (e) {
      const msg = e.response?.data?.message || 'No se pudo buscar el código.'
      setMensaje({ tipo: 'error', texto: '⚠ ' + msg })
    } finally {
      setBuscando(false)
      // Vuelve el foco al input para el próximo escaneo
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }

  const quitar = (idx) => setItems(prev => prev.filter((_, i) => i !== idx))

  // Andreani no se despacha a ciegas: primero se cotiza (Martin, 20 oct 2026:
  // "necesito que haya una instancia de cotización antes de armar la salida").
  // La cotización vive en Nueva salida (tabla por envío con tilde); se llega
  // con lo escaneado y Andreani ya asignado, y cotiza sola al abrir.
  const esAndreani = !!fleteros.find(f => String(f.id) === String(fleteroId))?.es_andreani
  const irACotizar = () => {
    const preseleccion = items.map(it => ({
      entidad_tipo: it.tipo,
      entidad_id: it.id,
      label: it.numero,
      sub: it.destinatario || it.cliente || '',
      calle: it.direccion || '',
      localidad: it.localidad || '',
      cp: it.cp || '',
      zona: it.zona || '',
    }))
    borrarBorrador(clave)   // la posta la toma Nueva salida (su state sobrevive al F5)
    onClose()
    navigate('/salidas/nueva', { state: { preseleccion, fletero_id: parseInt(fleteroId, 10), cotizar: true, fecha, notas } })
  }

  const despachar = async () => {
    if (items.length === 0) { setMensaje({ tipo: 'error', texto: 'Agregá al menos un documento.' }); return }
    if (!fleteroId) { setMensaje({ tipo: 'error', texto: 'Elegí un fletero.' }); return }
    setDespachando(true); setMensaje(null)
    try {
      // Retiro en sucursal: no hay salida. Cada remito pasa a retiro (el
      // backend lo libera si estaba en un viaje). Las NE no aplican.
      if (String(fleteroId).startsWith('retiro:')) {
        const sucursalId = String(fleteroId).slice(7)
        const ne = items.find(it => it.tipo !== 'remito')
        if (ne) { setMensaje({ tipo: 'error', texto: `⚠ ${ne.numero || ''}: una nota de envío no puede pasar a retiro (solo remitos T90).` }); return }
        for (const it of items) await remitosApi.pasarARetiro(it.id, sucursalId, notas)
        borrarBorrador(clave)
        onClose()
        navigate('/logistica/distribucion')
        return
      }
      // 0. ¿Alguno sale antes de la fecha pactada con el cliente? Se pregunta
      //    ANTES de crear la salida para no dejarla a medio armar.
      const { data: chk } = await salidasApi.verificarAnticipadas({
        fletero_id: parseInt(fleteroId, 10),
        fecha,
        documentos: items.map(it => ({ entidad_tipo: it.tipo, entidad_id: it.id })),
      })
      if (chk.entrega_anticipada?.length) {
        setFrenos(chk.entrega_anticipada)
        return
      }
      // 1. Crear salida
      const { data: salida } = await salidasApi.crear({
        fletero_id: parseInt(fleteroId, 10),
        fecha,
        notas: notas || null,
      })
      // 2. Agregar cada documento (en serie para que un error muestre cuál)
      for (const it of items) {
        await salidasApi.agregarDocumento(salida.id, { entidad_tipo: it.tipo, entidad_id: it.id })
      }
      // 3. Despachar (= 'armando' → 'despachada')
      await salidasApi.despachar(salida.id)
      borrarBorrador(clave)
      // 4. Navega al detalle de la salida. Desde ahí el operario imprime la
      //    hoja de ruta con el botón "🖨 Imprimir hoja de ruta" que ya
      //    funciona (descarga autenticada via blob).
      onClose()
      navigate(`/salidas/${salida.id}`)
    } catch (e) {
      if (e.response?.data?.entrega_anticipada) setFrenos(e.response.data.entrega_anticipada)
      const msg = e.response?.data?.message || 'Error al despachar.'
      setMensaje({ tipo: 'error', texto: '⚠ ' + msg })
    } finally {
      setDespachando(false)
    }
  }

  return (
    <div style={st.overlay}>
      <div style={st.box} onClick={e => e.stopPropagation()}>
        <div style={st.head}>
          <h3 style={st.title}>📷 Asignación rápida</h3>
          <Boton variante="ghost" sm onClick={cerrar}>✕ Cerrar</Boton>
        </div>
        {recuperado && (
          <div style={st.recuperado}>
            <div>
              <strong>Recuperamos tu asignación en curso</strong>
              {recuperado.guardado_at && <> (guardada a las {fmtHoraDe(recuperado.guardado_at)})</>}.
              {recuperado.quitados.length > 0 && (
                <div style={st.recuperadoQuitados}>
                  Ya no están disponibles y se sacaron de la lista:{' '}
                  {recuperado.quitados.map(q => `${q.numero} (${q.motivo})`).join(' · ')}
                </div>
              )}
            </div>
            <Boton variante="ghost" sm onClick={() => {
              borrarBorrador(clave)
              setItems([]); setFleteroId(''); setNotas(''); setRecuperado(null)
              setFecha(new Date().toISOString().slice(0, 10))
            }}>Descartar</Boton>
          </div>
        )}
        <p style={st.sub}>
          Escaneá el código de barras de cada remito (T90) o nota de envío (T80).
          Después elegí el fletero y despachá — se imprime la hoja de ruta y la salida pasa a distribución.
          Con Andreani, primero ves la cotización de cada envío y recién ahí se arma la salida.
        </p>

        {/* Scanner */}
        <div style={st.scanner}>
          <input
            ref={inputRef}
            style={st.scannerInput}
            type="text"
            value={codigo}
            placeholder="Escaneá o tipeá el código (ej. R001234 o R001234-01) y Enter"
            onChange={e => setCodigo(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); agregar() } }}
            disabled={buscando}
          />
          <Boton variante="primary" onClick={agregar} disabled={buscando || !codigo.trim()}>
            {buscando ? '...' : '+ Agregar'}
          </Boton>
        </div>

        <FrenoEntregaAnticipada frenos={frenos} />
        {mensaje && (
          <div style={mensaje.tipo === 'ok' ? st.msgOk : st.msgError}>{mensaje.texto}</div>
        )}

        {/* Lista de items */}
        {items.length > 0 && (
          <div style={st.lista}>
            <div style={st.listaHead}>{items.length} documento{items.length !== 1 ? 's' : ''} escaneado{items.length !== 1 ? 's' : ''}</div>
            {items.map((it, i) => (
              <div key={it.tipo + ':' + it.id} style={st.item}>
                <div style={st.itemInfo}>
                  <div style={st.itemNum}>
                    <span style={{ ...st.tipoTag, ...(it.tipo === 'remito' ? st.tagT90 : st.tagT80) }}>
                      {it.tipo === 'remito' ? 'T90' : 'T80'}
                    </span>
                    <strong>{it.numero}</strong>
                  </div>
                  <div style={st.itemMeta}>
                    {it.destinatario || it.cliente || '—'}
                    {it.direccion && <span style={st.muted}> · {it.direccion}</span>}
                    {it.localidad && <span style={st.muted}> · {it.localidad}</span>}
                    {it.zona && <span style={st.pillZona}>📍 {it.zona}</span>}
                  </div>
                </div>
                <Boton variante="ghost" sm onClick={() => quitar(i)}>✕</Boton>
              </div>
            ))}
          </div>
        )}

        {/* Fletero + fecha */}
        <div style={st.formGrid}>
          <div>
            <label style={st.label}>Destino</label>
            <select style={st.select} value={fleteroId} onChange={e => setFleteroId(e.target.value)}>
              <option value="">— Elegí un fletero o una sucursal —</option>
              {fleteros.map(f => (
                <option key={f.id} value={f.id}>{f.nombre}</option>
              ))}
              {sucursales.length > 0 && (
                <optgroup label="Retiro en sucursal (sin salida)">
                  {sucursales.map(sc => <option key={sc.id} value={`retiro:${sc.id}`}>🏬 Retira en {sc.nombre}</option>)}
                </optgroup>
              )}
            </select>
          </div>
          <div>
            <label style={st.label}>Fecha</label>
            <input style={st.input} type="date" value={fecha} onChange={e => setFecha(e.target.value)} />
          </div>
        </div>

        <div>
          <label style={st.label}>Notas (opcional)</label>
          <input style={st.input} type="text" value={notas} onChange={e => setNotas(e.target.value)} placeholder="Ej: salida urgente del mediodía" />
        </div>

        {/* Acciones */}
        <div style={st.actions}>
          <Boton variante="ghost" onClick={cerrar} disabled={despachando}>Cancelar</Boton>
          <Boton
            variante="primary"
            onClick={esAndreani ? irACotizar : despachar}
            disabled={despachando || items.length === 0 || !fleteroId || (frenos.length > 0 && !esAndreani)}
          >
            {esAndreani ? `💲 Cotizar ${items.length} envío(s) por Andreani →` : despachando ? 'Despachando…' : `🚚 Despachar (${items.length})`}
          </Boton>
        </div>
      </div>
    </div>
  )
}

// Vuelve a consultar cada documento del borrador con el mismo buscar-codigo
// del escaneo: el backend dice si sigue disponible (no entregado, no anulado,
// no asignado a otra salida). Un error de red no lo saca: se conserva.
async function revalidar(lista) {
  const vigentes = []
  const quitados = []
  for (const it of lista) {
    try {
      const { data } = await asignacionApi.buscarCodigo(it.numero)
      vigentes.push(data)
    } catch (e) {
      if (e.response && e.response.status >= 400 && e.response.status < 500) {
        quitados.push({ numero: it.numero, motivo: e.response.data?.message || 'ya no está disponible' })
      } else {
        vigentes.push(it)
      }
    }
  }
  return { vigentes, quitados }
}

const st = {
  recuperado: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem', background: colors.infoBg, border: `1px solid ${colors.infoBorder}`, color: colors.info, padding: '0.5rem 0.75rem', borderRadius: 6, fontSize: '0.85rem', marginBottom: '0.75rem' },
  recuperadoQuitados: { marginTop: 4, color: colors.warn },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1000 },
  box: { background: 'white', borderRadius: 10, padding: '1.25rem 1.5rem', maxWidth: 720, width: '100%', maxHeight: '90vh', overflowY: 'auto' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' },
  title: { margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b' },
  sub: { margin: '0 0 1rem', color: '#64748b', fontSize: '0.85rem', lineHeight: 1.5 },
  scanner: { display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' },
  scannerInput: { flex: 1, padding: '0.65rem 0.9rem', border: '2px solid #1e40af', borderRadius: 6, fontSize: '1rem', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
  msgOk: { background: '#dcfce7', color: '#166534', padding: '0.5rem 0.75rem', borderRadius: 6, fontSize: '0.85rem', marginBottom: '0.75rem' },
  msgError: { background: '#fee2e2', color: '#991b1b', padding: '0.5rem 0.75rem', borderRadius: 6, fontSize: '0.85rem', marginBottom: '0.75rem' },
  // Altura FIJA: la caja no crece con cada escaneo — scrollea adentro.
  lista: { background: '#f8fafc', borderRadius: 8, padding: '0.75rem', marginBottom: '1rem', height: 240, overflowY: 'auto', boxSizing: 'border-box' },
  listaHead: { fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' },
  item: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.65rem', background: 'white', borderRadius: 6, marginBottom: '0.35rem', border: '1px solid #e2e8f0' },
  itemInfo: { flex: 1, minWidth: 0 },
  itemNum: { display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem' },
  itemMeta: { fontSize: '0.78rem', color: '#475569', marginTop: '2px' },
  tipoTag: { fontSize: '0.65rem', fontWeight: 700, padding: '2px 6px', borderRadius: 4, letterSpacing: '0.05em' },
  tagT90: { background: '#eff6ff', color: '#1d4ed8' },
  tagT80: { background: '#f5f3ff', color: '#6d28d9' },
  pillZona: { background: '#ecfeff', color: '#0e7490', padding: '1px 6px', borderRadius: 4, marginLeft: '0.5rem', fontSize: '0.72rem', fontWeight: 600 },
  muted: { color: '#94a3b8' },
  formGrid: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' },
  label: { display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' },
  select: { width: '100%', padding: '0.55rem 0.75rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem', background: 'white' },
  input: { width: '100%', padding: '0.55rem 0.75rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem', borderTop: '1px solid #f1f5f9', paddingTop: '0.75rem' },
}
